import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from './AuthContext';
import { canOpenLetter } from '../utils/moonPhases';

const LetterNotificationContext = createContext();

export function useLetterNotification() {
  const context = useContext(LetterNotificationContext);
  if (!context) {
    throw new Error('useLetterNotification must be used within LetterNotificationProvider');
  }
  return context;
}

// Helper functions for read letters tracking
const getReadLettersKey = (userEmail) => `readLetters_${userEmail}`;
const getNotifiedLettersKey = (userEmail) => `notifiedLetters_${userEmail}`;
const getLastKnownOpenableKey = (userEmail) => `lastKnownOpenable_${userEmail}`;

const getReadLetters = (userEmail) => {
  if (!userEmail) return [];
  const stored = localStorage.getItem(getReadLettersKey(userEmail));
  return stored ? JSON.parse(stored) : [];
};

const markLetterAsReadInStorage = (userEmail, letterId) => {
  if (!userEmail || !letterId) return;
  const readLetters = getReadLetters(userEmail);
  if (!readLetters.includes(letterId)) {
    readLetters.push(letterId);
    localStorage.setItem(getReadLettersKey(userEmail), JSON.stringify(readLetters));
  }
};

// Track letters we've already notified about
const getNotifiedLetters = (userEmail) => {
  if (!userEmail) return [];
  const stored = localStorage.getItem(getNotifiedLettersKey(userEmail));
  return stored ? JSON.parse(stored) : [];
};

const markLetterAsNotified = (userEmail, letterId) => {
  if (!userEmail || !letterId) return;
  const notifiedLetters = getNotifiedLetters(userEmail);
  if (!notifiedLetters.includes(letterId)) {
    notifiedLetters.push(letterId);
    localStorage.setItem(getNotifiedLettersKey(userEmail), JSON.stringify(notifiedLetters));
  }
};

// Track which letters were openable last time we checked (to detect newly openable)
const getLastKnownOpenable = (userEmail) => {
  if (!userEmail) return [];
  const stored = localStorage.getItem(getLastKnownOpenableKey(userEmail));
  return stored ? JSON.parse(stored) : [];
};

const setLastKnownOpenable = (userEmail, openableIds) => {
  if (!userEmail) return;
  localStorage.setItem(getLastKnownOpenableKey(userEmail), JSON.stringify(openableIds));
};

export function LetterNotificationProvider({ children }) {
  const { currentUser } = useAuth();
  const [letterNotification, setLetterNotification] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [readLetters, setReadLetters] = useState([]);
  const [notifiedLetters, setNotifiedLetters] = useState([]);
  const isFirstLoad = useRef(true);

  // Load read and notified letters from localStorage when user changes
  useEffect(() => {
    if (currentUser?.email) {
      const userReadLetters = getReadLetters(currentUser.email);
      const userNotifiedLetters = getNotifiedLetters(currentUser.email);
      setReadLetters(userReadLetters);
      setNotifiedLetters(userNotifiedLetters);
    }
  }, [currentUser]);

  // Listen for new letters
  useEffect(() => {
    if (!currentUser) return;

    const lettersRef = ref(database, 'letters/entries');

    const unsubscribe = onValue(lettersRef, (snapshot) => {
      if (!snapshot.exists()) return;

      const letters = [];
      snapshot.forEach((childSnapshot) => {
        const letter = {
          id: childSnapshot.key,
          ...childSnapshot.val()
        };

        // Calculate if letter can be opened
        let canOpen = false;
        if (letter.sealMethod === 'moonPhase') {
          canOpen = canOpenLetter(letter.sealedMoonPhase);
        } else if (letter.sealMethod === 'timeDelay') {
          canOpen = new Date() >= new Date(letter.canOpenAt);
        }

        letter.canOpen = canOpen;
        letters.push(letter);
      });

      // Get letters from partner (not from current user)
      const partnerLetters = letters.filter(letter =>
        letter.authorEmail !== currentUser.email
      );

      // Get current read letters from state
      const currentReadLetters = getReadLetters(currentUser.email);
      const currentNotifiedLetters = getNotifiedLetters(currentUser.email);
      const lastKnownOpenable = getLastKnownOpenable(currentUser.email);

      // Find currently openable letters
      const currentlyOpenable = partnerLetters
        .filter(l => l.canOpen)
        .map(l => l.id);

      // Find letters that need notification:
      // 1. New letters that haven't been notified about yet (regardless of seal status)
      // 2. Letters that just became openable AND haven't been read yet
      const newLetters = partnerLetters.filter(letter =>
        !currentNotifiedLetters.includes(letter.id)
      );

      const newlyOpenableUnread = partnerLetters.filter(letter =>
        letter.canOpen &&
        !currentReadLetters.includes(letter.id) &&
        !lastKnownOpenable.includes(letter.id)
      );

      // Update last known openable state
      setLastKnownOpenable(currentUser.email, currentlyOpenable);

      // Determine what to notify about
      let lettersToNotify = [];

      if (isFirstLoad.current) {
        // On first load, only notify about unread letters that can be opened
        lettersToNotify = partnerLetters.filter(letter =>
          letter.canOpen && !currentReadLetters.includes(letter.id)
        );
        isFirstLoad.current = false;
      } else {
        // After first load, notify about:
        // - Brand new letters (never notified)
        // - Letters that just became openable and haven't been read
        lettersToNotify = [
          ...newLetters,
          ...newlyOpenableUnread.filter(l => !newLetters.find(nl => nl.id === l.id))
        ];
      }

      // Mark new letters as notified
      newLetters.forEach(letter => {
        markLetterAsNotified(currentUser.email, letter.id);
      });

      // Update notified letters state
      setNotifiedLetters(getNotifiedLetters(currentUser.email));

      // Show notification if there are letters to notify about
      if (lettersToNotify.length > 0) {
        // Sort by createdAt descending (newest first)
        lettersToNotify.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateB - dateA;
        });

        const latestLetter = lettersToNotify[0];

        // Determine notification type
        let notificationType = 'new';
        if (newlyOpenableUnread.find(l => l.id === latestLetter.id) &&
            !newLetters.find(l => l.id === latestLetter.id)) {
          notificationType = 'nowReadable';
        }

        // Count total unread openable letters
        const unreadOpenable = partnerLetters.filter(letter =>
          letter.canOpen && !currentReadLetters.includes(letter.id)
        );

        const newNotification = {
          letterId: latestLetter.id,
          author: latestLetter.author,
          authorEmail: latestLetter.authorEmail,
          sealMethod: latestLetter.sealMethod,
          canOpen: latestLetter.canOpen,
          createdAt: latestLetter.createdAt,
          unreadCount: unreadOpenable.length,
          notificationType: notificationType
        };

        setLetterNotification(newNotification);
        setIsVisible(true);
      } else {
        // No new notifications needed, but check if we should hide
        const unreadOpenable = partnerLetters.filter(letter =>
          letter.canOpen && !currentReadLetters.includes(letter.id)
        );

        if (unreadOpenable.length === 0) {
          setIsVisible(false);
          setLetterNotification(null);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser, readLetters]);

  // Dismiss notification - hides mail temporarily
  const dismissNotification = () => {
    setIsVisible(false);
  };

  // Function to mark a letter as read
  const markAsRead = (letterId) => {
    if (currentUser?.email && letterId) {
      markLetterAsReadInStorage(currentUser.email, letterId);
      const updatedReadLetters = getReadLetters(currentUser.email);
      setReadLetters(updatedReadLetters);
    }
  };

  // Function to check if a letter is read
  const isLetterRead = (letterId) => {
    return readLetters.includes(letterId);
  };

  const value = {
    letterNotification,
    isVisible,
    dismissNotification,
    markAsRead,
    isLetterRead,
    readLetters
  };

  return (
    <LetterNotificationContext.Provider value={value}>
      {children}
    </LetterNotificationContext.Provider>
  );
}
